import * as React from "react";
import {Dialog, DialogActions, DialogContent, DialogTitle, TextField, Paper, PaperProps} from "@mui/material";
import Button from "@mui/material/Button";
import {PDFViewer} from "@react-pdf/renderer";
import {Print} from "@mui/icons-material";
import {GroupQueryRequest, WoodGroupsList} from "../../../types/models/MergeGroups.ts";
import {useGetTimberClassMultipleQuery} from "../../../apis/TimberClass.api.ts";
import {PrimaryTimberClass} from "../../../types/models/TimberClass.ts";
import {PrimaryTimberType} from "../../../types/models/TimberType.ts";
import {PrimaryShipment} from "../../../types/models/Shipment.ts";
import {PrimaryTimberStorage} from "../../../types/models/TimberStorage.ts";
import {PrimaryCutting} from "../../../types/models/Cutting.ts";
import {useGetTimberTypeMultipleQuery} from "../../../apis/TimberType.api.ts";
import {useGetBuyerQuery} from "../../../apis/Buyer.api.ts";
import {useGetCuttingQuery} from "../../../apis/Cutting.api.ts";
import {useGetTimberStorageMultipleQuery} from "../../../apis/TimberStorage.api.ts";
import {useGetShipmentQuery} from "../../../apis/Shipment.api.ts";
import {PrimaryBuyer} from "../../../types/models/Buyer.ts";
import GenericRootReportPdf, {GroupQueryRequestPdf} from "./GenericRootReportPdf.tsx";
import { Trans } from "@lingui/react/macro";
import GradientCircularProgress from "../../../../share/components/progress/GradientCircularProgress.tsx";

function PaperComponent(props: PaperProps) {
    return (
        <Paper {...props} />
    );
}

type IGenericRootReportPdfDialogProps = {
    title: string;
    groupedData: WoodGroupsList;
    filter: GroupQueryRequest;
    copies?: number;
};

const GenericRootReportPdfDialog: React.FC<IGenericRootReportPdfDialogProps> = ({groupedData, title, filter, copies = 1}) => {

    const [open, setOpen] = React.useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const timberClass = useGetTimberClassMultipleQuery(filter?.classId?.map((id) => PrimaryTimberClass.fromId(id)), {skip: !filter?.classId?.length})
    const timberType = useGetTimberTypeMultipleQuery(filter?.typeId?.map((id) => PrimaryTimberType.fromId(id)), {skip: !filter?.typeId?.length})
    const storage = useGetTimberStorageMultipleQuery(filter?.storageId?.map((id) => PrimaryTimberStorage.fromId(id)), {skip: !filter?.storageId?.length})
    const buyer = useGetBuyerQuery(PrimaryBuyer.fromId(filter?.buyerId), {skip: !filter?.buyerId})
    const cutting = useGetCuttingQuery(PrimaryCutting.fromId(filter?.cuttingId), {skip: !filter?.cuttingId})
    const shipment = useGetShipmentQuery(PrimaryShipment.fromId(filter?.shipmentId), {skip: !filter?.shipmentId})

    const filterPdf: GroupQueryRequestPdf = {
        ...filter,
        timberClass: timberClass?.data,
        timberType: timberType?.data,
        buyer: buyer?.data,
        cutting: cutting?.data,
        storage: storage?.data,
        shipment: shipment?.data,
    };

    return (
        <>
            <Button onClick={handleClickOpen}><Print/></Button>
            <Dialog
                open={open && groupedData}
                onClose={handleClose}
                PaperComponent={PaperComponent}
                maxWidth="lg"
                PaperProps={{
                    style: {
                        width: '70%',     // Custom width
                        height: '850px'     // Custom height
                    },
                }}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    {
                        timberClass.isLoading || timberClass.isFetching ||
                        timberType.isLoading || timberType.isFetching ||
                        buyer.isLoading || buyer.isFetching ||
                        cutting.isLoading || cutting.isFetching ||
                        storage.isLoading || storage.isFetching ||
                        shipment.isLoading || shipment.isFetching ||
                        groupedData.isLoading
                        ?
                        (<GradientCircularProgress/>)
                        :
                        (<div>
                            <PDFViewer showToolbar={true} width="100%" height="700px">
                                <GenericRootReportPdf
                                    groupedData={groupedData}
                                    filter={filterPdf}
                                    title={title}
                                    numberOfCopies={copies}
                                />
                            </PDFViewer>
                        </div>)
                    }
                </DialogContent>
                <DialogActions>
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'row' }}>
                        <Trans>Number of copies</Trans>: {copies}
                    </Paper>
                    <Button onClick={handleClose} color="primary">
                        <Trans>Close</Trans>
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default GenericRootReportPdfDialog
